import { supabase } from "@/integrations/supabase/client";

export const USERNAME_REGEX = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const USERNAME_MIN = 2;
export const USERNAME_MAX = 40;
export const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Convert any user-provided string into a URL-safe lowercase slug.
 * Mirrors the Postgres slugify_username function.
 */
export function slugifyUsername(input: string): string {
  if (!input) return "";
  let s = input.toLowerCase().trim();
  // Replace common Swedish/diacritic characters
  const map: Record<string, string> = {
    å: "a", ä: "a", ö: "o", é: "e", è: "e", ê: "e", ü: "u",
    á: "a", à: "a", â: "a",
  };
  s = s.replace(/[åäöéèêüáàâ]/g, (c) => map[c] ?? c);
  s = s.replace(/[\s_]+/g, "-");
  s = s.replace(/[^a-z0-9-]/g, "");
  s = s.replace(/-+/g, "-");
  s = s.replace(/^-+|-+$/g, "");
  return s;
}

export function isValidUsernameFormat(username: string): boolean {
  return (
    USERNAME_REGEX.test(username) &&
    username.length >= USERNAME_MIN &&
    username.length <= USERNAME_MAX
  );
}

/**
 * Check via RPC whether a username is currently free.
 * Returns true when available; false when taken or invalid.
 */
export async function isUsernameAvailable(username: string): Promise<boolean> {
  if (!isValidUsernameFormat(username)) return false;
  const { data, error } = await (supabase as any).rpc("is_username_available", {
    _username: username,
  });
  if (error) {
    console.error("is_username_available rpc error", error);
    return false;
  }
  return data === true;
}

/**
 * Generate a unique username from a base string by appending -2, -3, ... until free.
 */
export async function generateUniqueUsername(base: string): Promise<string> {
  const seed = slugifyUsername(base) || "medlem";
  let candidate = seed;
  let n = 1;
  // Hard cap to avoid pathological loops
  while (n < 100 && !(await isUsernameAvailable(candidate))) {
    n += 1;
    candidate = `${seed}-${n}`;
  }
  return candidate;
}

/**
 * Build the public profile URL for a user.
 * Prefers username, falls back to UUID (PublicProfile resolves both).
 */
export function profileHref(p: {
  username?: string | null;
  id?: string | null;
}): string {
  return `/saljare/${p.username || p.id || ""}`;
}
